import { Tracking } from './Tracking';
import { GameItemConfig, ItemIDs } from '../../Config/Items';
import {
    TGameAreas,
    TransactionActionTypes,
    TransactionTypes,
} from '../../DeltaTracking/ResourceParamBuilder';
import Core from '../../Core/Core';
import { Views } from '../../State/reducers/view';
import {
    isSocialRecieve,
    isSocialSend,
    SocialActionTypes,
} from '../../DeltaTracking/SocialParamBuilder';
import { DDNAEvents } from '../../DeltaTracking/Tracker';
import { IRealCurrency, ITransactionProduct } from '../../DeltaTracking/TransactionParamBuilder';
import Product = FBInstant.Product;

export default class ResourceTracking {
    public static boosterBought(id: string, usedAd: boolean = false) {
        let dim = '';
        switch (id) {
            case ItemIDs.HINT:
                dim = 'TapOnBuyMagnifier';
                break;
            case ItemIDs.HIGHLIGHT:
                dim = 'TapOnBuyFlashlight';
                break;
            case ItemIDs.FIRECRACKER:
                dim = 'TapOnBuyFireworks';
                break;
            case ItemIDs.MAGNET:
                dim = 'TapOnBuyMagnet';
                break;
            case ItemIDs.SHUFFLE:
                dim = 'TapOnBuyShuffle';
                break;
            case ItemIDs.TIME:
                dim = 'TapOnBuyClock';
                break;
        }

        if (dim !== '') {
            Tracking.addTrack({
                event: 'Design',
                dimension1: dim,
            });

            this.sendBuyResourceEvent(id, 1, TGameAreas.LEVEL, usedAd);
        }
    }

    public static heartBought() {
        Tracking.addTrack({
            event: 'Design',
            dimension1: 'TapOnBuyHeart',
        });

        const tArea =
            Core.store.getState().StateView === Views.MAP ? TGameAreas.MAP : TGameAreas.LEVEL;
        this.sendBuyResourceEvent('heart', 1, tArea);
    }

    public static sendCoinsResourceEvent(
        amount: number,
        transaction: TransactionTypes,
        tAction: TransactionActionTypes,
        tArea: TGameAreas,
        socialAction?: SocialActionTypes
    ) {
        if (!Core.delta) {
            return;
        }

        Core.delta.addTrackedEvent(DDNAEvents.RESOURCE, {
            tCoinsRecieved: amount > 0 ? amount : 0,
            tCoinsUsed: amount < 0 ? amount * -1 : 0,
            tEventType: transaction,
            tActionType: tAction,
            adID: 'none',
            socialActionType: socialAction || SocialActionTypes.NONE,
            socialSend: isSocialSend(socialAction),
            socialRecieve: isSocialRecieve(socialAction),
            tMagnifyingGlassReceived: 0,
            tMagnifyingGlassUsed: 0,
            tFlashLightReceived: 0,
            tFlashlightUsed: 0,
            tFireCrackerReceived: 0,
            tFireCrackerUsed: 0,
            tTimeReceived: 0,
            tTimeUsed: 0,
            tMagnetReceived: 0,
            tMagnetUsed: 0,
            tShuffleReceived: 0,
            tShuffleUsed: 0,
            tGameArea: tArea,
        });
    }

    public static sendBuyResourceEvent(
        id: string,
        amt: number,
        tArea: TGameAreas,
        usedAd: boolean = false
    ) {
        if (!Core.delta) {
            return;
        }

        Core.delta.addTrackedEvent(DDNAEvents.RESOURCE, {
            tCoinsRecieved: 0,
            tCoinsUsed: GameItemConfig[id].price,
            tEventType: usedAd ? TransactionTypes.AD : TransactionTypes.COIN_PURCHASE,
            tActionType: TransactionActionTypes.INVENTORY_PURCHASE,
            adID: 'none',
            socialActionType: SocialActionTypes.NONE,
            socialSend: 0,
            socialRecieve: 0,
            tMagnifyingGlassReceived: id === ItemIDs.HINT ? amt : 0,
            tMagnifyingGlassUsed: 0,
            tFlashLightReceived: id === ItemIDs.HIGHLIGHT ? amt : 0,
            tFlashlightUsed: 0,
            tFireCrackerReceived: id === ItemIDs.FIRECRACKER ? amt : 0,
            tFireCrackerUsed: 0,
            tMagnetReceived: id === ItemIDs.MAGNET ? amt : 0,
            tMagnetUsed: 0,
            tTimeReceived: id === ItemIDs.TIME ? amt : 0,
            tTimeUsed: 0,
            tShuffleReceived: id === ItemIDs.SHUFFLE ? amt : 0,
            tShuffleUsed: 0,
            tGameArea: tArea,
        });
    }

    public static sendUsedResourceEvent(id: string, amt: number, tArea: TGameAreas) {
        if (!Core.delta) {
            return;
        }

        const coinIds = ['coin', 'coins', ItemIDs.COINS];

        Core.delta.addTrackedEvent(DDNAEvents.RESOURCE, {
            tCoinsRecieved: 0,
            tCoinsUsed: coinIds.includes(id) ? amt : 0,
            tEventType: TransactionTypes.INVENTORY,
            tActionType: TransactionActionTypes.BOOSTER_USE,
            adID: 'none',
            socialActionType: SocialActionTypes.NONE,
            socialSend: 0,
            socialRecieve: 0,
            tMagnifyingGlassReceived: 0,
            tMagnifyingGlassUsed: id === ItemIDs.HINT ? amt : 0,
            tFlashLightReceived: 0,
            tFlashlightUsed: id === ItemIDs.HIGHLIGHT ? amt : 0,
            tFireCrackerReceived: 0,
            tFireCrackerUsed: id === ItemIDs.FIRECRACKER ? amt : 0,
            tMagnetReceived: 0,
            tMagnetUsed: id === ItemIDs.MAGNET ? amt : 0,
            tTimeReceived: 0,
            tTimeUsed: id === ItemIDs.TIME ? amt : 0,
            tShuffleReceived: 0,
            tShuffleUsed: id === ItemIDs.SHUFFLE ? amt : 0,
            tGameArea: tArea,
        });
    }

    public static sendRewardResourceEvent(
        id: string,
        amt: number,
        tAction: TransactionActionTypes,
        tArea: TGameAreas
    ) {
        if (!Core.delta) {
            return;
        }

        const coinIds = ['coin', 'coins', ItemIDs.COINS];

        Core.delta.addTrackedEvent(DDNAEvents.RESOURCE, {
            tCoinsRecieved: coinIds.includes(id) ? amt : 0,
            tCoinsUsed: 0,
            tEventType: TransactionTypes.REWARD,
            tActionType: tAction,
            adID: 'none',
            socialActionType: SocialActionTypes.NONE,
            socialSend: 0,
            socialRecieve: 0,
            tMagnifyingGlassReceived: id === ItemIDs.HINT ? amt : 0,
            tMagnifyingGlassUsed: 0,
            tFlashLightReceived: id === ItemIDs.HIGHLIGHT ? amt : 0,
            tFlashlightUsed: 0,
            tFireCrackerReceived: id === ItemIDs.FIRECRACKER ? amt : 0,
            tFireCrackerUsed: 0,
            tMagnetReceived: id === ItemIDs.MAGNET ? amt : 0,
            tMagnetUsed: 0,
            tShuffleReceived: id === ItemIDs.SHUFFLE ? amt : 0,
            tShuffleUsed: 0,
            tGameArea: tArea,
        });
    }

    public static sendTransactionEvent(product: Product, tArea: TGameAreas) {
        const productsReceived = getPurchaseProductReceived(product);
        const fbEventName = 'IAP_Purchase_' + product.productID;
        Tracking.addTrack({
            eventName: fbEventName,
            valueToSum: productsReceived.realCurrency.realCurrencyAmount,
        });

        if (!Core.delta) {
            return;
        }

        Core.delta.addTrackedEvent(DDNAEvents.TRANSACTION, {
            transactionType: TransactionTypes.PURCHASE,
            tActionType: TransactionActionTypes.INVENTORY_PURCHASE, // will this always be the same?..the others do not seem to fit with real transaction
            tGameArea: tArea,
            productsReceived,
        });
    }
}

function getPurchaseProductReceived(product: Product): ITransactionProduct {
    const realCurrencyAmount: number = parseInt(product.price.replace(/\D/g, ''), 10) || 0;
    const realCurrencyType: string = product.priceCurrencyCode || 'EUR';

    const currency: IRealCurrency = {
        realCurrencyAmount,
        realCurrencyType,
    };

    return {
        realCurrency: {
            ...currency,
        },
    };
}
